import React from 'react';
import {
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';

// ksm_frontend ApprovalV2HistoryPanel.js 포팅(2026-10-20 결재 V2 이관) - 결재 문서의
// 단계(steps) + 액션 이력(actions)을 보여준다. 결재함(V2)/수주품의서 상세/휴가 상세 등
// ApprovalV2 문서를 보여줄 곳이면 어디서든 재사용한다.

export const APPROVAL_V2_STEP_STATUS_LABELS = {
  WAITING: '대기',
  PENDING: '처리대기',
  APPROVED: '승인',
  REJECTED: '반려',
  CANCELED: '취소',
  SKIPPED: '생략',
  DELEGATED: '대결',
};

export const APPROVAL_V2_STEP_STATUS_COLORS = {
  WAITING: 'default',
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
  CANCELED: 'default',
  SKIPPED: 'default',
  DELEGATED: 'info',
};

export const APPROVAL_V2_ACTION_TYPE_LABELS = {
  SUBMIT: '상신',
  APPROVE: '승인',
  REJECT: '반려',
  RECALL: '회수',
  CANCEL: '취소',
  SKIP: '생략',
  DELEGATE_APPLY: '대결적용',
  AUTO_APPROVE: '전결(자동승인)',
  LINE_CHANGED: '결재선변경',
};

const formatDt = (value) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-');

const ApprovalV2HistoryPanel = ({ steps = [], actions = [] }) => {
  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        결재선 단계
      </Typography>
      <List dense disablePadding>
        {steps.map((step) => (
          <ListItem key={step.apprDocStepId} sx={{ px: 0, alignItems: 'flex-start' }}>
            <ListItemText
              primary={(
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" fontWeight={600}>
                    {step.stepOrder}단계
                  </Typography>
                  <Chip
                    label={APPROVAL_V2_STEP_STATUS_LABELS[step.stepStatus] || step.stepStatus}
                    color={APPROVAL_V2_STEP_STATUS_COLORS[step.stepStatus] || 'default'}
                    size="small"
                  />
                  {step.delegationId && <Chip label="대결" color="info" size="small" variant="outlined" />}
                </Stack>
              )}
              secondary={(
                <Stack spacing={0.3} sx={{ mt: 0.3 }}>
                  <Typography variant="body2" color="text.secondary">
                    결재자: {step.currentAssigneeNm || step.originalAssigneeNm || '-'}
                    {step.currentAssigneeUsrUuid &&
                      step.originalAssigneeUsrUuid &&
                      step.currentAssigneeUsrUuid !== step.originalAssigneeUsrUuid &&
                      ` (원결재자: ${step.originalAssigneeNm || '-'})`}
                  </Typography>
                  {step.actedAt && (
                    <Typography variant="body2" color="text.secondary">
                      처리일: {formatDt(step.actedAt)}
                    </Typography>
                  )}
                  {step.actionComment && (
                    <Typography variant="body2" color="text.secondary">
                      의견: {step.actionComment}
                    </Typography>
                  )}
                </Stack>
              )}
            />
          </ListItem>
        ))}
        {steps.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            결재 단계 정보가 없습니다.
          </Typography>
        )}
      </List>

      <Divider sx={{ my: 1.5 }} />

      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        액션 이력
      </Typography>
      <List dense disablePadding>
        {actions.map((action) => (
          <ListItem key={action.actionId} sx={{ px: 0 }}>
            <ListItemText
              primary={(
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    label={APPROVAL_V2_ACTION_TYPE_LABELS[action.actionType] || action.actionType}
                    size="small"
                    variant="outlined"
                  />
                  <Typography variant="body2">{action.actorUsrNm || '-'}</Typography>
                  {action.onBehalfOfUsrNm && (
                    <Typography variant="caption" color="text.secondary">
                      (원결재자 {action.onBehalfOfUsrNm})
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {formatDt(action.actionAt)}
                  </Typography>
                </Stack>
              )}
              secondary={action.actionComment}
            />
          </ListItem>
        ))}
        {actions.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            액션 이력이 없습니다.
          </Typography>
        )}
      </List>
    </Box>
  );
};

export default ApprovalV2HistoryPanel;
